import React from 'react';
import {
  BankOutlined,
  CheckCircleFilled,
  DisconnectOutlined,
  DownOutlined,
  ExclamationCircleFilled,
  PlusOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import {
  FundingChip,
  bankInitials,
  bankLogoSrc,
  fundingChipAccountKind,
  fundingChipLabel,
} from './linked-account-parts';

const PiggyBankOutlined: React.FC<{ className?: string }> = ({ className }) => (
  <span className={`anticon ${className ?? ''}`} aria-hidden>
    <svg
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M19 5c-1.5 0-2.8 1.4-3 2-3.5-1.5-11-.3-11 5 0 1.8 0 3 2 4.5V20h4v-2h3v2h4v-4c1-.5 1.7-1 2-2h2v-4h-2c0-1-.5-1.5-1-2V5z" />
      <path d="M2 9v1c0 1.1.9 2 2 2h1" />
      <path d="M16 11h.01" />
    </svg>
  </span>
);

const typeIcon = (chip: FundingChip) => {
  const kind = fundingChipAccountKind(chip.accountType);
  const className = 'funding-bank-chip-type';
  if (kind === 'savings') {
    return <PiggyBankOutlined className={className} />;
  }
  if (kind === 'checking') {
    return <BankOutlined className={className} aria-hidden />;
  }
  return <WalletOutlined className={className} aria-hidden />;
};

const statusIcon = (chip: FundingChip) => {
  if (chip.status === 'not_set') {
    return null;
  }
  if (chip.status === 'pending') {
    return (
      <ExclamationCircleFilled
        className="funding-bank-chip-status is-pending"
        aria-label="Apex is still approving this bank"
      />
    );
  }
  if (chip.status === 'disconnected') {
    return (
      <DisconnectOutlined
        className="funding-bank-chip-status is-disconnected"
        aria-label="Connection lost"
      />
    );
  }
  return (
    <CheckCircleFilled
      className="funding-bank-chip-status is-connected"
      aria-label="Bank connected"
    />
  );
};

export const FundingBankChipContent: React.FC<{ chip: FundingChip; showChevron?: boolean }> = ({
  chip,
  showChevron = true,
}) => {
  const isAddChip = chip.status === 'not_set';
  const imageSrc = bankLogoSrc(chip.bankLogo);
  return (
    <>
      {isAddChip ? (
        <PlusOutlined className="funding-bank-chip-plus" aria-hidden />
      ) : imageSrc ? (
        <img src={imageSrc} alt="" className="funding-bank-chip-logo" />
      ) : (
        <span className="funding-bank-chip-initials" aria-hidden>
          {bankInitials(chip.bankName)}
        </span>
      )}
      {!isAddChip && typeIcon(chip)}
      <span className="funding-bank-chip-text">{fundingChipLabel(chip)}</span>
      {statusIcon(chip)}
      {showChevron ? <DownOutlined className="funding-bank-chip-chevron" aria-hidden /> : null}
    </>
  );
};

export const FundingBankChipStatic: React.FC<{ chip: FundingChip; emptyLabel?: string }> = ({
  chip,
  emptyLabel = 'No funding bank',
}) =>
  chip.status === 'not_set' ? (
    <span className="wavvest-accounts-funding-text">{emptyLabel}</span>
  ) : (
    <span
      className={`funding-bank-chip is-${chip.status} is-readonly`}
      title={chip.bankName ? `Funded by ${chip.bankName}` : undefined}
    >
      <FundingBankChipContent chip={chip} showChevron={false} />
    </span>
  );
